// features/EditVideo/InlineVideoEditor.tsx

import {
    type FC,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import ReactPlayer from 'react-player'
import ReactSlider from 'react-slider'
import type SwiperCore from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'

import { IconPauseFill, IconPlayOutline } from 'assets/svg/sprite'
import { useVideoPlayerOrientation } from 'helpers/helperHooks'
import { formatVideoTime } from 'lib/dayjs'
import { type VIDEO_ORIENTATION } from 'store/streaming'

import './_editVideoModal.scss'

type InlineVideoEditorProps = {
    vidSrc: string
    storageSrc: string
    thumbnails?: string[]
    start?: number
    end?: number
    orientation?: VIDEO_ORIENTATION | string
    maxDuration?: number
    hidePlayButton?: boolean
isUploading?: boolean
    onRangeChange?: (data: {
        startTime: number
        endTime: number
        path: string
    }) => void
}

const FRAME_COUNT = 12
const DEFAULT_MAX_DURATION = 60

const clamp = (value: number, min: number, max: number) => {
    return Math.min(Math.max(value, min), max)
}

const isUsableVideoSrc = (src?: string) => {
    if (!src) return false

    return (
        src.startsWith('blob:') ||
        src.startsWith('data:') ||
        src.startsWith('http://') ||
        src.startsWith('https://') ||
        src.startsWith('/')
    )
}

const generateVideoFrames = async (
    src: string,
    count = FRAME_COUNT
): Promise<string[]> => {
    return await new Promise((resolve, reject) => {
        const video = document.createElement('video')
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
            reject(new Error('Could not create canvas context'))
            return
        }

        let duration = 0
        let currentIndex = 0
        const frames: string[] = []

        const cleanup = () => {
            video.pause()
            video.removeAttribute('src')
            video.load()
        }

        video.crossOrigin = 'anonymous'
        video.preload = 'metadata'
        video.muted = true
        video.playsInline = true
        video.src = src

        video.onloadedmetadata = () => {
            duration = video.duration || 0

            canvas.width = video.videoWidth || 160
            canvas.height = video.videoHeight || 90

            video.currentTime = 0
        }

        video.onseeked = () => {
            try {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
                frames.push(canvas.toDataURL('image/jpeg', 0.7))

                currentIndex += 1

                if (currentIndex >= count) {
                    cleanup()
                    resolve(frames)
                    return
                }

                const nextTime =
                    count <= 1 ? 0 : (duration / (count - 1)) * currentIndex

                video.currentTime = clamp(nextTime, 0, duration)
            } catch (error) {
                cleanup()
                reject(error)
            }
        }

        video.onerror = () => {
            cleanup()
            reject(new Error('Could not load video for frame generation'))
        }
    })
}

const InlineVideoEditor: FC<InlineVideoEditorProps> = ({
    vidSrc,
    storageSrc,
    thumbnails,
    orientation,
    start,
    end,
    hidePlayButton = false,
isUploading = false,
    maxDuration = DEFAULT_MAX_DURATION,
    onRangeChange
}) => {
    const [sliderValues, setSliderValues] = useState<number[]>([0, 0, 100])
    const [playing, setPlaying] = useState(false)
    const [trimStart, setTrimStart] = useState<number>(start ?? 0)
    const [trimEnd, setTrimEnd] = useState<number>(0)
    const [videoDuration, setVideoDuration] = useState<number>(NaN)
    const [playerReady, setPlayerReady] = useState(false)
    const [needsReset, setNeedsReset] = useState(false)
    const [ended, setEnded] = useState(false)
    const [generatedFrames, setGeneratedFrames] = useState<string[]>([])
    const [currentTime, setCurrentTime] = useState<number>(start ?? 0)

    const playerRef = useRef<ReactPlayer>(null)
    const swiperRef = useRef<SwiperCore>()
    const sliderWrapperRef = useRef<HTMLDivElement>(null)

    const lockedVidSrcRef = useRef<string | null>(null)
    const initializedRef = useRef(false)
    const lastRangeRef = useRef<{
        startTime: number
        endTime: number
        path: string
    } | null>(null)

    const { deviceOrientation, playerWidth, playerHeight } =
        useVideoPlayerOrientation(orientation)

    if (!lockedVidSrcRef.current && isUsableVideoSrc(vidSrc)) {
        lockedVidSrcRef.current = vidSrc
    }

    const lockedVidSrc = lockedVidSrcRef.current || vidSrc

    const frameImages = useMemo(() => {
        if (generatedFrames.length) return generatedFrames
        if (thumbnails?.length) return thumbnails
        return []
    }, [generatedFrames, thumbnails])

    const getTimeFromPercent = useCallback(
        (percent: number) => {
            if (!isFinite(videoDuration) || videoDuration <= 0) return 0

            return clamp((percent / 100) * videoDuration, 0, videoDuration)
        },
        [videoDuration]
    )

    const getPercentFromTime = useCallback(
        (time: number) => {
            if (!isFinite(videoDuration) || videoDuration <= 0) return 0

            return clamp((time / videoDuration) * 100, 0, 100)
        },
        [videoDuration]
    )

    const moveProgressThumb = useCallback(
        (time: number) => {
            if (!sliderWrapperRef.current) return
            if (!isFinite(videoDuration) || videoDuration <= 0) return
            if (trimEnd <= trimStart) return

            const thumbs =
                sliderWrapperRef.current.querySelectorAll<HTMLElement>(
                    '.slider-thumb'
                )

            const progressThumb = thumbs[1]

            if (!progressThumb) return

            const safeTime = clamp(time, trimStart, trimEnd)

            const percent = clamp(
                getPercentFromTime(safeTime),
                getPercentFromTime(trimStart),
                getPercentFromTime(trimEnd)
            )

            progressThumb.style.left = `${percent}%`
            progressThumb.style.willChange = 'left'
        },
        [videoDuration, trimStart, trimEnd, getPercentFromTime]
    )

    const seekToTime = useCallback(
        (time: number) => {
            if (!playerRef.current || !isFinite(videoDuration)) {
                return
            }

            if (trimEnd <= trimStart) {
                return
            }

            const safeTime = clamp(time, trimStart, trimEnd)

            setCurrentTime(safeTime)

            setSliderValues(prev => [
                prev[0],
                getPercentFromTime(safeTime),
                prev[2]
            ])

            moveProgressThumb(safeTime)
            playerRef.current.seekTo(safeTime, 'seconds')
        },
        [
            videoDuration,
            trimStart,
            trimEnd,
            getPercentFromTime,
            moveProgressThumb
        ]
    )

    useEffect(() => {
        if (!lockedVidSrc) {
            setGeneratedFrames([])
            return
        }

        let cancelled = false

        generateVideoFrames(lockedVidSrc, FRAME_COUNT)
            .then(frames => {
                if (!cancelled) {
                    setGeneratedFrames(frames)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setGeneratedFrames([])
                }
            })

        return () => {
            cancelled = true
        }
    }, [lockedVidSrc])

    useLayoutEffect(() => {
        if (
            playerRef.current &&
            playerReady &&
            !playing &&
            !isFinite(videoDuration) &&
            !needsReset
        ) {
            playerRef.current.seekTo(1e101, 'seconds')
            setNeedsReset(true)
        }
    }, [needsReset, playerReady, playing, videoDuration])

    useLayoutEffect(() => {
        const intervalId = setInterval(() => {
            if (
                needsReset &&
                playerRef.current &&
                isFinite(playerRef.current.getDuration()) &&
                !isFinite(videoDuration)
            ) {
                const duration = playerRef.current.getDuration()

                setVideoDuration(duration)
                setNeedsReset(false)
                clearInterval(intervalId)
            }
        }, 50)

        return () => clearInterval(intervalId)
    }, [needsReset, videoDuration])

    useEffect(() => {
        if (initializedRef.current) {
            return
        }

        if (!isFinite(videoDuration) || videoDuration <= 0) {
            return
        }

        initializedRef.current = true

        const initialStart = clamp(start ?? 0, 0, videoDuration)

        const requestedEnd =
            typeof end === 'number' && end > initialStart
                ? end
                : initialStart + maxDuration

        const initialEnd = clamp(
            requestedEnd,
            initialStart,
            Math.min(initialStart + maxDuration, videoDuration)
        )

        const nextSliderValues = [
            getPercentFromTime(initialStart),
            getPercentFromTime(initialStart),
            getPercentFromTime(initialEnd)
        ]

        setTrimStart(initialStart)
        setTrimEnd(initialEnd)
        setCurrentTime(initialStart)
        setPlaying(false)
        setEnded(false)
        setSliderValues(nextSliderValues)

        playerRef.current?.seekTo(initialStart, 'seconds')

        requestAnimationFrame(() => {
            moveProgressThumb(initialStart)
        })
    }, [
        videoDuration,
        maxDuration,
        start,
        end,
        getPercentFromTime,
        moveProgressThumb
    ])

    useEffect(() => {
        if (!playing) return
        if (!playerRef.current) return
        if (!isFinite(videoDuration)) return
        if (trimEnd <= trimStart) return

        let frameId: number
        let lastTimerUpdate = 0

        const tick = () => {
            const player = playerRef.current

            if (!player) return

            const rawTime = player.getCurrentTime()
            const safeTime = clamp(rawTime, trimStart, trimEnd)

            moveProgressThumb(safeTime)

            const now = performance.now()

            if (now - lastTimerUpdate > 100) {
                lastTimerUpdate = now

                setCurrentTime(prev => {
                    if (Math.abs(prev - safeTime) < 0.05) return prev
                    return safeTime
                })
            }

            if (rawTime >= trimEnd) {
                setEnded(true)
                setPlaying(false)
                player.seekTo(trimEnd, 'seconds')
                moveProgressThumb(trimEnd)
                setCurrentTime(trimEnd)

                setSliderValues(prev => [
                    prev[0],
                    getPercentFromTime(trimEnd),
                    prev[2]
                ])

                return
            }

            frameId = requestAnimationFrame(tick)
        }

        frameId = requestAnimationFrame(tick)

        return () => cancelAnimationFrame(frameId)
    }, [
        playing,
        videoDuration,
        trimStart,
        trimEnd,
        moveProgressThumb,
        getPercentFromTime
    ])

    useEffect(() => {
        if (!onRangeChange) return
        if (!initializedRef.current) return
        if (!isFinite(videoDuration)) return
        if (trimEnd <= trimStart) return

        const last = lastRangeRef.current

        const changed =
            !last ||
            Math.abs(last.startTime - trimStart) > 0.03 ||
            Math.abs(last.endTime - trimEnd) > 0.03 ||
            last.path !== storageSrc

        if (!changed) return

        lastRangeRef.current = {
            startTime: trimStart,
            endTime: trimEnd,
            path: storageSrc
        }

        onRangeChange({
            startTime: trimStart,
            endTime: trimEnd,
            path: storageSrc
        })
    }, [onRangeChange, trimStart, trimEnd, storageSrc, videoDuration])

    if (!deviceOrientation) {
        return null
    }

    return (
        <div
            className={`editVideoModal editVideoModal--inline ${
                deviceOrientation.type.includes('portrait')
                    ? 'editVideoModal--portrait'
                    : ''
            } ${
                deviceOrientation.type.includes('landscape')
                    ? 'editVideoModal--landscape'
                    : ''
            }`}
        >
            <div className='editVideoModal__player'>
                <div className='editVideoModal__player__wrapper'>
{!hidePlayButton && (
  <div
    className={`editVideoModal__player__icon editVideoModal__player__icon${
      playing ? '--playing' : '--notPlaying'
    }`}
    onClick={() => {
      setEnded(false)

      if (ended || currentTime >= trimEnd) {
        seekToTime(trimStart)
      }

      setPlaying(prev => !prev)
    }}
  >
    {playing ? <IconPauseFill /> : <IconPlayOutline color='white'/>}
  </div>
)}

                    <ReactPlayer
                        ref={playerRef}
                        url={lockedVidSrc}
                        width={playerWidth}
                        height={playerHeight}
                        className='editVideoModal__player__video'
                        style={{ display: 'flex' }}
                        controls={false}
                        muted={false}
                        playing={playing}
                        progressInterval={100}
                        onReady={() => {
                            setPlayerReady(true)
                        }}
                        onDuration={duration => {
                            if (isFinite(duration) && duration > 0) {
                                setVideoDuration(duration)
                            }
                        }}
                        onPlay={() => {
                            setEnded(false)

                            if (
                                currentTime < trimStart ||
                                currentTime >= trimEnd
                            ) {
                                seekToTime(trimStart)
                            }

                            setPlaying(true)
                        }}
                        onPause={() => {
                            setPlaying(false)
                        }}
                        onEnded={() => {
                            setEnded(true)
                            setPlaying(false)
                            seekToTime(trimEnd)
                        }}
                        onProgress={({ playedSeconds }) => {
                            if (!playing) return
                            if (!isFinite(videoDuration)) return
                            if (trimEnd <= trimStart) return

                            if (playedSeconds >= trimEnd) {
                                setEnded(true)
                                setPlaying(false)
                                seekToTime(trimEnd)
                            }
                        }}
                    />
                </div>

<div
  className={`editVideoModal__slider ${
    sliderValues[0] > 0 ? 'slid-right' : ''
  } ${sliderValues[2] < 100 ? 'slid-left' : ''} ${
    isUploading ? 'editVideoModal__slider--uploading' : ''
  }`}
>
                    <div className='editVideoModal__slider__timer'>
                        {`${formatVideoTime(currentTime)} / ${formatVideoTime(trimEnd)}`}
                    </div>

                    <Swiper
                        onInit={(core: SwiperCore) => {
                            swiperRef.current = core
                        }}
                        allowTouchMove={false}
                        slidesPerView={FRAME_COUNT}
                    >
                        {frameImages.map((thumb, idx) => (
                            <SwiperSlide key={`${thumb}-${idx}`}>
                                <div className='editVideoModal__slider__image'>
                                    <img src={thumb} alt='' draggable={false} />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <div
                        ref={sliderWrapperRef}
                        className='editVideoModal__slider__wrapper'
                    >
                        <ReactSlider
                            className='horizontal-slider'
                            thumbClassName='slider-thumb'
                            trackClassName='slider-track'
                            value={sliderValues}
                            min={0}
                            max={100}
                            pearling={false}
                            minDistance={0}
                            onBeforeChange={() => {
                                setEnded(false)
                                setPlaying(false)
                            }}
                            onChange={(value, index) => {
                                if (
                                    !isFinite(videoDuration) ||
                                    videoDuration <= 0
                                ) {
                                    return
                                }

                                const nextValues = value as number[]

                                setEnded(false)
                                setPlaying(false)

                                const currentStartPercent =
                                    getPercentFromTime(trimStart)

                                const currentEndPercent =
                                    getPercentFromTime(trimEnd)

                                if (index === 0) {
                                    const requestedStartTime =
                                        getTimeFromPercent(nextValues[0])

                                    const minStartTime = Math.max(
                                        0,
                                        trimEnd - maxDuration
                                    )

                                    const maxStartTime = trimEnd

                                    const nextStartTime = clamp(
                                        requestedStartTime,
                                        minStartTime,
                                        maxStartTime
                                    )

                                    const nextPlayTime = clamp(
                                        currentTime,
                                        nextStartTime,
                                        trimEnd
                                    )

                                    setTrimStart(nextStartTime)
                                    setCurrentTime(nextPlayTime)

                                    setSliderValues([
                                        getPercentFromTime(nextStartTime),
                                        getPercentFromTime(nextPlayTime),
                                        currentEndPercent
                                    ])

                                    playerRef.current?.seekTo(
                                        nextPlayTime,
                                        'seconds'
                                    )

                                    requestAnimationFrame(() => {
                                        moveProgressThumb(nextPlayTime)
                                    })

                                    return
                                }

                                if (index === 2) {
                                    const requestedEndTime =
                                        getTimeFromPercent(nextValues[2])

                                    const minEndTime = trimStart

                                    const maxEndTime = Math.min(
                                        videoDuration,
                                        trimStart + maxDuration
                                    )

                                    const nextEndTime = clamp(
                                        requestedEndTime,
                                        minEndTime,
                                        maxEndTime
                                    )

                                    const nextPlayTime = clamp(
                                        currentTime,
                                        trimStart,
                                        nextEndTime
                                    )

                                    setTrimEnd(nextEndTime)
                                    setCurrentTime(nextPlayTime)

                                    setSliderValues([
                                        currentStartPercent,
                                        getPercentFromTime(nextPlayTime),
                                        getPercentFromTime(nextEndTime)
                                    ])

                                    playerRef.current?.seekTo(
                                        nextPlayTime,
                                        'seconds'
                                    )

                                    requestAnimationFrame(() => {
                                        moveProgressThumb(nextPlayTime)
                                    })

                                    return
                                }

                                const requestedPlayTime =
                                    getTimeFromPercent(nextValues[1])

                                const nextPlayTime = clamp(
                                    requestedPlayTime,
                                    trimStart,
                                    trimEnd
                                )

                                setCurrentTime(nextPlayTime)

                                setSliderValues([
                                    currentStartPercent,
                                    getPercentFromTime(nextPlayTime),
                                    currentEndPercent
                                ])

                                playerRef.current?.seekTo(
                                    nextPlayTime,
                                    'seconds'
                                )

                                requestAnimationFrame(() => {
                                    moveProgressThumb(nextPlayTime)
                                })
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default InlineVideoEditor